import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Camera, CameraOff, AlertTriangle, ShieldCheck, ShieldAlert } from "lucide-react";
import axios from "axios";
import { API_BASE as API_URL } from "@/config/api";

export default function WebcamMonitor({ gameId, token, stake = 0, onAlert }) {
  const [isActive, setIsActive] = useState(false);
  const [hasPermission, setHasPermission] = useState(null);
  const [lastAnalysis, setLastAnalysis] = useState(null);
  const [alertCount, setAlertCount] = useState(0);
  const [stream, setStream] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const frameCountRef = useRef(0);
  const intervalRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  // Check if webcam is required
  const webcamRequired = stake >= 100;

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      stopMonitoring();
    };
  }, []);

  const startMonitoring = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: "user" }
      });
      
      setStream(mediaStream);
      setHasPermission(true);
      setIsActive(true);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play();
      }
      
      // Start analyzing frames every 10 seconds
      intervalRef.current = setInterval(() => {
        captureAndAnalyze();
      }, 10000);
      
      // Capture first frame after 2 seconds
      setTimeout(captureAndAnalyze, 2000);
      
      toast.success("Мониторинг включен");
      
    } catch (err) {
      console.error("Webcam error:", err);
      setHasPermission(false);
      toast.error("Не удалось получить доступ к камере");
    }
  };

  const stopMonitoring = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    
    setIsActive(false);
  };

  const captureAndAnalyze = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    
    // Set canvas size to match video
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    
    // Draw current frame
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Convert to base64
    const frameData = canvas.toDataURL("image/jpeg", 0.7).split(",")[1];
    
    frameCountRef.current += 1;
    
    try {
      const response = await axios.post(
        `${API_URL}/games/${gameId}/webcam-frame`,
        {
          game_id: gameId,
          frame_data: frameData,
          frame_number: frameCountRef.current
        },
        { headers }
      );
      
      setLastAnalysis(response.data);
      
      if (response.data.suspicious) {
        setAlertCount(prev => prev + 1);
        onAlert?.(response.data);
        
        if (response.data.confidence > 0.7) {
          toast.warning("⚠️ Обнаружена подозрительная активность", {
            description: response.data.alerts?.join(", ") || "Проверьте условия игры"
          });
        }
      }
      
    } catch (err) {
      console.error("Frame analysis error:", err);
    }
  };

  // Don't render if not required for low-stake games
  if (!webcamRequired) {
    return null;
  }

  return (
    <div className="webcam-monitor space-y-3">
      {/* Status Badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isActive ? (
            <Badge variant="outline" className="text-green-500 border-green-500">
              <ShieldCheck className="h-3 w-3 mr-1" />
              Мониторинг активен
            </Badge>
          ) : (
            <Badge variant="outline" className="text-yellow-500 border-yellow-500">
              <ShieldAlert className="h-3 w-3 mr-1" />
              Требуется камера
            </Badge>
          )}
          
          {alertCount > 0 && (
            <Badge variant="destructive">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {alertCount} алертов
            </Badge>
          )}
        </div>
        
        <Button
          size="sm"
          variant={isActive ? "destructive" : "outline"}
          onClick={isActive ? stopMonitoring : startMonitoring}
        >
          {isActive ? (
            <>
              <CameraOff className="h-4 w-4 mr-2" />
              Выключить
            </>
          ) : (
            <>
              <Camera className="h-4 w-4 mr-2" />
              Включить
            </>
          )}
        </Button>
      </div>

      {/* Video Preview (small) */}
      {isActive && (
        <div className="relative">
          <video
            ref={videoRef}
            className="w-full max-w-[200px] rounded-lg border border-border/50"
            autoPlay
            playsInline
            muted
          />
          <canvas ref={canvasRef} className="hidden" />
          
          {/* Recording indicator */}
          <div className="absolute top-2 left-2 flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-xs text-white bg-black/50 px-1 rounded">REC</span>
          </div>
        </div>
      )}

      {/* Last Analysis Result */}
      {lastAnalysis && (
        <div className={`text-xs p-2 rounded ${
          lastAnalysis.suspicious 
            ? "bg-red-500/10 text-red-500 border border-red-500/30" 
            : "bg-green-500/10 text-green-500 border border-green-500/30"
        }`}>
          {lastAnalysis.suspicious ? (
            <>
              <AlertTriangle className="h-3 w-3 inline mr-1" />
              {lastAnalysis.alerts?.join(", ") || "Подозрительная активность"}
            </>
          ) : (
            <>
              <ShieldCheck className="h-3 w-3 inline mr-1" />
              Проверка пройдена
            </>
          )}
        </div>
      )}

      {/* Permission denied message */}
      {hasPermission === false && (
        <div className="text-xs text-red-500 bg-red-500/10 p-2 rounded">
          ⚠️ Доступ к камере запрещён. Для игр на деньги требуется включить камеру.
        </div>
      )}
    </div>
  );
}
